import { DirectDataInputEntity } from "domain/entity/DirectDataInput/DirectDataInputEntity";
import { DirectDataInputConstant } from "presentation/constant/DirectDataInput/DirectDataInputConstant";
import { MessageConstant } from "presentation/constant/MessageConstant";
import { WorkspaceConstant } from "presentation/constant/WorkSpaceConstant";
import { useDirectDataInputVM } from "presentation/hook/DirectDataInput/useDirectDataInputVM";
import { useMessageBarVM } from "presentation/hook/useMessageBar";
import { useANAInfoTracked } from "presentation/store/ANAInfo";
import { useDirectDataInputTracked } from "presentation/store/DirectDataInput/DirectDataInputProvider";
import { ConfirmDeleteModal } from "presentation/view/components/ConfirmDeleteModal";
import { HeaderTitle } from "presentation/view/components/HeaderWithBackButton/HeaderTitle";
import { NbisIconButton } from "presentation/view/components/NbisIconButton";
import { memo, useCallback, useState } from "react";
import { HPHButton, IconButton, Loader } from "veronica-ui-component/dist/component/core";
import { Sidebarheader, StyledAction } from "veronica-ui-component/dist/component/core/styled/uiFramework.styled";

const DirectDataInputTitleBar: React.FC = () => {
    const [directDataInputState] = useDirectDataInputTracked();
    const directDataInputVM = useDirectDataInputVM();
    const [isLoading, setIsLoading] = useState(false);
    const { selectedRows, searchCriteria } = directDataInputState;
    const messageBarVM = useMessageBarVM();
    const [isShowDelete, setIsShowDelete] = useState<boolean>(false);
    const [deletedRows, setDeletedRows] = useState<DirectDataInputEntity[]>();
    const [anaInfoState] = useANAInfoTracked();
    const { allowDelete, allowUpdate, allowCreate } = anaInfoState;

    const handleAdd = useCallback(() => {
        directDataInputVM.onAddClick();
    }, [directDataInputVM]);

    const handleDelete = useCallback(async () => {
        if (!deletedRows) return;
        setIsLoading(true);
        setIsShowDelete(false);
        const ids = deletedRows.map(entity => entity.id);
        directDataInputVM.onDelete(ids).then((data) => {
            if (data && data.success) {
                messageBarVM.showSuccess(MessageConstant.common.DELETED_DATA_SUCCESSFUL)
                directDataInputVM.onSearch(searchCriteria).then(data => {
                    setIsLoading(false);
                });
            } else {
                messageBarVM.showError(data.data);
            }
        }).catch((error) => {
            setIsLoading(false);
        }).finally(() => {
            setIsLoading(false);
        });
    }, [deletedRows, directDataInputVM, messageBarVM, searchCriteria]);

    const isDeleteDisable = () => {
        if (selectedRows.length > 0) return false
        return true;
    }

    const handleDeleteClick = useCallback(async () => {
        setIsShowDelete(true);
        setDeletedRows(selectedRows);
    }, [selectedRows]);

    const handleSearch = useCallback(async () => {
        directDataInputVM.onSearchClick();
    }, [directDataInputVM]);

    const handleCancel = () => {
        setIsShowDelete(false);
    }

    const handleUnconfirmClick = useCallback(async () => {
        if (selectedRows.length === 0) {
            messageBarVM.showError("Please select a record.");
            return;
        }
        setIsLoading(true);
        const ids = selectedRows.map(entity => entity.id);
        directDataInputVM.onUnconfirm(ids).then(async (data) => {
            if (data && data.success) {
                messageBarVM.showSuccess("Unconfirm successfully.");
                directDataInputVM.onSearch(searchCriteria).then((data) => {
                    setIsLoading(false);
                })
            } else {
                setIsLoading(false);
                messageBarVM.showError(data.data);
            }
        }).catch(error => {
            setIsLoading(false);
        });

    }, [directDataInputVM, messageBarVM, searchCriteria, selectedRows]);

    const handleConfirmClick = useCallback(async () => {
        if (selectedRows.length === 0) {
            messageBarVM.showError("Please select a record.");
            return;
        }
        setIsLoading(true);
        const ids = selectedRows.map(entity => entity.id);
        directDataInputVM.onConfirm(ids).then(async (data) => {
            if (data && data.success) {
                messageBarVM.showSuccess("Confirm successfully.");
                directDataInputVM.onSearch(searchCriteria).then((data) => {
                    setIsLoading(false);
                })
            } else {
                setIsLoading(false);
                messageBarVM.showError(data.data);
            }
        }).catch(error => {
            setIsLoading(false);
        });

    }, [directDataInputVM, messageBarVM, searchCriteria, selectedRows]);

    const isConfirmDisable = () => {
        if (selectedRows.length > 0 && selectedRows.every(entity => entity.status === 'NEW')) return false
        return true;
    }

    const isUnConfirmDisable = () => {
        if (selectedRows.length > 0 && selectedRows.every(entity => entity.status === 'CONFIRMED')) return false
        return true;
    }

    return <Sidebarheader style={{ width: '100%', display: "flex", alignItems: "center" }}>
        <HeaderTitle>{DirectDataInputConstant.TITLE}</HeaderTitle>
        {(isLoading) && <Loader Indicator="Spinner" size="Medium" />}
        <StyledAction className="tm-animated-wrapper">
            <IconButton fileName='Icon-search' disabled={false} size='medium' toolTipText={'Search'} toolTipArrow={false} onClick={handleSearch} />
            {(allowCreate) && <div className="add-seperator" />}
            {(allowCreate) && <NbisIconButton fileName='Icon-add' disabled={false} size='medium' toolTipText={'Add'} toolTipArrow={false} onClick={handleAdd} />}
            {(allowDelete) && <div className="add-seperator" />}
            {(allowDelete) && <HPHButton disabled={isDeleteDisable()} label={WorkspaceConstant.Common.BUTTON_DELETE} size={'Small'} theme={'Secondary'} onClick={handleDeleteClick} />}
            {(allowUpdate) && <div className="add-seperator" />}
            {(allowUpdate) && <HPHButton disabled={isUnConfirmDisable()} label={WorkspaceConstant.Common.BUTTON_UNCONFIRM} size={'Small'} theme={'Secondary'} onClick={handleUnconfirmClick} />}
            {(allowUpdate) && <HPHButton disabled={isConfirmDisable()} label={WorkspaceConstant.Common.BUTTON_CONFIRM} size={'Small'} theme={'Primary'} onClick={handleConfirmClick} />}
            <ConfirmDeleteModal headerText={`${WorkspaceConstant.Common.BUTTON_DELETE} ${DirectDataInputConstant.TITLE}`}
                contentText={`${MessageConstant.common.DELETE_ALERT_TITLE}\n${MessageConstant.common.DELETE_ALERT_MESSAGE}`}
                visible={isShowDelete}
                onCancelClick={handleCancel}
                onDeleteClick={handleDelete}
            />
        </StyledAction>
        {directDataInputState.forceRefresh && <></>}
    </Sidebarheader>
}

export default memo(DirectDataInputTitleBar);